import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { DockGlyph } from './dock-icons';
import { LORE_DATABASE, type LoreCheckData, type LoreEntry } from './bit-brew-constants';

interface LoreArchiveProps {
  checkData: LoreCheckData;
  currentBeans: number;
  onLoreUnlocked: (loreId: string, title: string) => void;
  onPurchaseLore: (loreId: string, cost: number) => void;
  purchasedLoreIds: string[];
  // Effective lore set — the selected shop's custom stories when it has them,
  // else the default LORE_DATABASE. Same ids/conditions, different words.
  loreDb?: LoreEntry[];
  currencyLabel?: string; // per-shop points word (040); default BEANS
  embedded?: boolean; // kept for prop-surface stability — the PASS tab supplies its own layout now
}

export default function LoreArchive({ checkData, currentBeans, onLoreUnlocked, onPurchaseLore, purchasedLoreIds, loreDb = LORE_DATABASE, currencyLabel }: LoreArchiveProps) {
  const [page, setPage] = useState(0);
  const [expandedLore, setExpandedLore] = useState<LoreEntry | null>(null);
  const cardsPerPage = 6;
  const totalPages = Math.ceil(loreDb.length / cardsPerPage);
  const prevUnlocked = useRef<Set<string> | null>(null);

  const unlockedIds = new Set([
    ...loreDb.filter((lore) => lore.unlockCheck(checkData)).map((lore) => lore.id),
    ...purchasedLoreIds,
  ]);

  useEffect(() => {
    // On first render, seed prevUnlocked with current state so we don't
    // fire onLoreUnlocked for items that were already unlocked before mount.
    if (prevUnlocked.current === null) {
      prevUnlocked.current = new Set(unlockedIds);
      return;
    }
    unlockedIds.forEach((id) => {
      if (!prevUnlocked.current!.has(id)) {
        const lore = loreDb.find((l) => l.id === id);
        if (lore) onLoreUnlocked(id, lore.title);
      }
    });
    prevUnlocked.current = new Set(unlockedIds);
  }, [checkData.level, checkData.totalPurchases, checkData.beans, checkData.nftCount, checkData.questsCompleted, checkData.shopCount, purchasedLoreIds.length]);

  const pageCards = loreDb.slice(page * cardsPerPage, (page + 1) * cardsPerPage);

  return (
    <div className="flex flex-col gap-3">
      {/* Header with nav */}
      <div className="flex justify-between items-center">
        <div>
          <p className="font-heading text-[9px] tracking-[1px] uppercase text-body-grey flex items-center gap-2 mb-1">
            <DockGlyph icon="lore" className="w-3 h-3" /> COFFEE LORE
          </p>
          <p className="font-heading text-[8px] text-body-grey">SPENDABLE: {currentBeans} {currencyLabel || 'BEANS'}</p>
        </div>
        <div className="flex items-center gap-2 pixel-corners-sm bg-black/[.18] text-white font-grotesk font-bold px-3 py-2">
          <button
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            className="disabled:opacity-30"
          >
            <ChevronLeft size={16} />
          </button>
          <span className="font-heading text-[8px]">
            {page + 1}/{totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page === totalPages - 1}
            className="disabled:opacity-30"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* Lore cards: white for unlocked, muted for locked, gold for buyable legendaries */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {pageCards.map((lore) => {
          const isUnlocked = unlockedIds.has(lore.id);
          const isPurchasable = !!lore.cost && !isUnlocked;
          const canAfford = isPurchasable && currentBeans >= (lore.cost || 0);

          if (isUnlocked) {
            return (
              <motion.div
                key={lore.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                onClick={() => setExpandedLore(lore)}
                className="bg-white pixel-corners p-4 min-h-[120px] flex flex-col justify-center cursor-pointer hover:bg-background transition-colors"
              >
                <div className="flex items-baseline justify-between gap-[10px] mb-2">
                  <h3 className="font-grotesk font-bold text-[17px] text-primary">{lore.title}</h3>
                  <span className={`shrink-0 font-heading text-[7px] ${lore.cost ? 'text-gold-ink' : 'text-body-grey'}`}>
                    {lore.cost ? 'LEGENDARY' : 'UNLOCKED'}
                  </span>
                </div>
                <p className="font-grotesk font-medium text-[13px] text-body-grey leading-relaxed line-clamp-4">{lore.text}</p>
                <div className="h-2 pixel-bar pixel-corners-sm [--bar-gap:#f2ead6] bg-tab-inactive overflow-hidden mt-3">
                  <div className="h-full bg-pixel-cyan" style={{ width: '100%' }} />
                </div>
                <p className="font-heading text-[7px] text-body-grey mt-2">TAP TO READ</p>
              </motion.div>
            );
          }

          if (isPurchasable) {
            return (
              <div key={lore.id} className="bg-accent-alt pixel-corners p-[18px] flex flex-col justify-center">
                <div className="flex items-baseline justify-between gap-[10px]">
                  <p className="font-grotesk font-bold text-[17px] text-primary">{lore.title}</p>
                  <span className="shrink-0 font-heading text-[8px] text-gold-ink">LEGENDARY</span>
                </div>
                <p className="font-grotesk font-medium text-[13px] text-gold-ink leading-relaxed mt-2 mb-[14px]">{lore.text}</p>
                <button
                  onClick={(e) => { e.stopPropagation(); canAfford && onPurchaseLore(lore.id, lore.cost!); }}
                  disabled={!canAfford}
                  className={`w-full pixel-corners-sm bg-white border-2 border-primary text-primary font-grotesk font-bold text-[13px] py-3 ${
                    canAfford ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'
                  }`}
                >
                  {canAfford ? `Unlock for ${lore.cost} ${currencyLabel || 'beans'}` : `Need ${(lore.cost || 0) - currentBeans} more ${currencyLabel || 'beans'}`}
                </button>
              </div>
            );
          }

          return (
            <div key={lore.id} className="bg-white pixel-corners p-4 min-h-[120px] flex flex-col items-center justify-center text-center opacity-70">
              <DockGlyph icon="lore" className="w-5 h-5 text-body-grey mb-2" />
              <p className="font-heading text-[8px] text-body-grey">LOCKED</p>
              <p className="font-grotesk font-medium text-[12px] text-body-grey mt-2">UNLOCK: {lore.condition}</p>
              <div className="h-2 w-full pixel-bar pixel-corners-sm [--bar-gap:#f2ead6] bg-tab-inactive overflow-hidden mt-3">
                <div className="h-full bg-pixel-cyan" style={{ width: '0%' }} />
              </div>
            </div>
          );
        })}
      </div>

      {/* ═══ Expanded Lore Modal ═══ */}
      <AnimatePresence>
        {expandedLore && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center safe-modal bg-black/70 backdrop-blur-sm"
            onClick={() => setExpandedLore(null)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="w-full max-w-2xl max-h-full overflow-y-auto bg-white pixel-corners border-[3px] border-primary p-5 sm:p-8 relative"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <button
                onClick={() => setExpandedLore(null)}
                className="absolute top-1 right-1 p-3 text-body-grey hover:text-primary transition-colors"
              >
                <X size={20} />
              </button>

              {/* Badge */}
              {expandedLore.cost && (
                <div className="mb-4">
                  <span className="font-heading text-[9px] text-gold-ink pixel-corners-sm px-3 py-1.5 bg-accent-alt/40">LEGENDARY</span>
                </div>
              )}

              {/* Title */}
              <h3 className="font-grotesk font-bold text-[22px] text-primary mb-6 leading-tight">
                {expandedLore.title}
              </h3>

              {/* Lore text — readable size */}
              <p className="font-grotesk font-medium text-[15px] text-body-grey leading-relaxed">
                {expandedLore.text}
              </p>

              {/* Unlock condition */}
              <div className="mt-6 pt-4 border-t border-hairline">
                <p className="font-heading text-[9px] text-body-grey">
                  UNLOCKED VIA: {expandedLore.condition}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
